import React, { useCallback, useId, useMemo, useState } from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { Button } from "@coval/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@coval/ui/dropdown-menu"
import { Input } from "@coval/ui/input"
import { MultiSelect } from "@coval/ui/multi-select"
import { NumberInput } from "@coval/ui/number-input"
import { Tooltip } from "@coval/ui/tooltip"
import { CornerDownRight, Info, ListFilter, X } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import {
  FLOAT_COMPARISON_OPERATOR,
  SET_COMPARISON_OPERATOR,
  type ComparisonOperator,
  type FloatComparisonOperator,
  type MetricRecordData,
  type SetComparisonOperator,
  type TargetCondition,
} from "@/types/metrics/contracts"

import { INFO_ICON_STYLES, LABEL_ROW_STYLES, LABEL_STYLES } from "../styles"

interface TargetFilterToggleProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  valueType?: TargetFilterValueType
}

export type TargetFilterValueType =
  | "binary"
  | "boolean"
  | "categorical"
  | "numerical"
  | "string"

const comparisonOperators = {
  manager_numerical_llm_judge: {
    eq: "= equals",
    neq: "≠ doesn't equal",
    gt: "> greater than",
    gte: "≥ greater than or equal",
    lt: "< less than",
    lte: "≤ less than or equal",
  },
  manager_llm_binary: {
    in: "= equals",
  },
  manager_default_categorical: {
    in: "∈ contains",
    nin: "∉ doesn't contain",
  },
  string: {
    in: "is any of",
    nin: "is none of",
  },
  boolean: {
    eq: "= equals",
  },
}

export const TargetFilterToggle: React.FC<TargetFilterToggleProps> = ({
  metricRecordData,
  onChange,
  valueType,
}) => {
  const { trackEvent } = useAnalytics()
  // A numerical criterion always carries a target, so an emptied field saves
  // nothing; it is flagged until a number is typed or focus leaves and the
  // stored target is shown again.
  const [isThresholdEmpty, setIsThresholdEmpty] = useState(false)
  const thresholdMessageId = useId()

  const metricManager =
    (metricRecordData?.metric_manager as keyof typeof comparisonOperators) || ""

  const metricType = useMemo<TargetFilterValueType>(() => {
    if (valueType) {
      return valueType
    }
    if (metricManager.endsWith("categorical")) {
      return "categorical"
    }
    if (metricManager.endsWith("llm_binary")) {
      return "binary"
    }
    return "numerical"
  }, [metricManager, valueType])

  const currentOperators =
    metricType === "string"
      ? comparisonOperators.string
      : metricType === "boolean"
        ? comparisonOperators.boolean
        : (comparisonOperators[metricManager] ??
          (metricType === "categorical"
            ? comparisonOperators.manager_default_categorical
            : metricType === "binary"
              ? comparisonOperators.manager_llm_binary
              : comparisonOperators.manager_numerical_llm_judge))

  // Default operator for the current metric manager
  const defaultOperator = useMemo(() => {
    if (metricType === "numerical") {
      return valueType
        ? FLOAT_COMPARISON_OPERATOR.GTE
        : FLOAT_COMPARISON_OPERATOR.EQ
    }
    if (metricType === "boolean") {
      return FLOAT_COMPARISON_OPERATOR.EQ
    }
    return SET_COMPARISON_OPERATOR.IN
  }, [metricType, valueType])

  const assignDefaultTargetCondition = useCallback(() => {
    // Track with posthog
    trackEvent(TRACKING_EVENTS.METRICS.TARGET_CONDITION_CREATED, {
      organization_id: metricRecordData.organization_id,
      organization_slug: metricRecordData.organization_slug,
      metric_name: metricRecordData.metric_name,
      metric_display_name: metricRecordData.metric_display_name,
    })

    let target_condition: TargetCondition

    if (metricType === "categorical") {
      target_condition = {
        comparison_operator: defaultOperator as SetComparisonOperator,
        target_values: [],
      }
    } else if (metricType === "binary") {
      target_condition = {
        comparison_operator: defaultOperator as SetComparisonOperator,
        target_values: [ANNOTATION_VALUE.YES],
      }
    } else if (metricType === "string") {
      target_condition = {
        comparison_operator: defaultOperator as SetComparisonOperator,
        target_values: [""],
      }
    } else if (metricType === "boolean") {
      target_condition = {
        comparison_operator: defaultOperator as FloatComparisonOperator,
        target_float: 1,
      }
    } else {
      target_condition = {
        comparison_operator: defaultOperator as FloatComparisonOperator,
        target_float: 0,
      }
    }

    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        target_condition,
      },
    })
  }, [metricRecordData, onChange, defaultOperator, metricType, trackEvent])

  const removeTargetCondition = useCallback(() => {
    setIsThresholdEmpty(false)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        target_condition: undefined,
      },
    })
  }, [metricRecordData, onChange])

  // Initialize state from existing metadata - use useMemo to make it reactive
  const existingTargetCondition = useMemo(() => {
    return metricRecordData?.metric_metadata?.target_condition
  }, [metricRecordData?.metric_metadata?.target_condition])

  // Compute values directly from existingTargetCondition (falling back to defaults)
  const comparisonOperator = useMemo(() => {
    const existingOperator = existingTargetCondition?.comparison_operator
    if (
      metricType === "string" &&
      existingOperator !== SET_COMPARISON_OPERATOR.IN &&
      existingOperator !== SET_COMPARISON_OPERATOR.NIN
    ) {
      return SET_COMPARISON_OPERATOR.IN
    }
    return existingOperator || defaultOperator
  }, [
    existingTargetCondition?.comparison_operator,
    defaultOperator,
    metricType,
  ])

  const targetFloat = useMemo(() => {
    return existingTargetCondition && "target_float" in existingTargetCondition
      ? existingTargetCondition.target_float
      : undefined
  }, [existingTargetCondition])

  const targetBinaryValue = useMemo(() => {
    return existingTargetCondition &&
      "target_values" in existingTargetCondition &&
      existingTargetCondition.target_values?.[0]
      ? existingTargetCondition.target_values[0]
      : ANNOTATION_VALUE.YES
  }, [existingTargetCondition])

  const targetBooleanValue = useMemo(() => {
    return existingTargetCondition && "target_float" in existingTargetCondition
      ? existingTargetCondition.target_float !== 0
      : true
  }, [existingTargetCondition])

  const targetStringValues = useMemo(() => {
    return existingTargetCondition && "target_values" in existingTargetCondition
      ? existingTargetCondition.target_values
      : []
  }, [existingTargetCondition])

  const editableStringValues =
    targetStringValues.length > 0 ? targetStringValues : [""]

  // Categories are stored comma-separated, often with a space after each
  // comma; trimming matches them to the stored target values.
  const categories = useMemo(
    () =>
      metricRecordData?.metric_metadata?.additional_information?.value
        ?.split(",")
        .map((category) => category.trim())
        .filter((category) => category.length > 0) ?? [],
    [metricRecordData?.metric_metadata?.additional_information?.value],
  )

  const targetCategoricalValues = useMemo(() => {
    return existingTargetCondition && "target_values" in existingTargetCondition
      ? existingTargetCondition.target_values
      : []
  }, [existingTargetCondition])

  // A stored value that only differs from a category by surrounding spaces
  // selects that category, so toggling it never adds a second copy; the
  // stored form is kept until the user changes the selection.
  const selectedCategoricalValues = useMemo(
    () =>
      targetCategoricalValues.map((value) =>
        categories.includes(value.trim()) ? value.trim() : value,
      ),
    [categories, targetCategoricalValues],
  )

  // A stored value missing from the metric's categories stays selectable, so
  // it is shown and kept rather than dropped on the next change.
  const categoricalOptions = useMemo(
    () =>
      [...new Set([...categories, ...selectedCategoricalValues])].map(
        (category) => ({ value: category, label: category }),
      ),
    [categories, selectedCategoricalValues],
  )

  // Helper function to update metadata based on metric type
  const updateTargetMetadata = (
    comparisonOp: ComparisonOperator,
    floatValue?: number,
    targetValues?: Iterable<string>,
  ) => {
    const newMetadata = { ...metricRecordData.metric_metadata }

    if (metricType === "numerical" || metricType === "boolean") {
      if (floatValue !== undefined) {
        onChange({
          ...metricRecordData,
          metric_metadata: {
            ...newMetadata,
            target_condition: {
              comparison_operator: comparisonOp as FloatComparisonOperator,
              target_float: floatValue,
            },
          },
        })
      }
    } else {
      const values = targetValues ? Array.from(targetValues) : []

      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...newMetadata,
          target_condition: {
            comparison_operator: comparisonOp as SetComparisonOperator,
            target_values: values,
          },
        },
      })
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className={LABEL_ROW_STYLES}>
        <span className={LABEL_STYLES}>Success Criteria</span>
        <Tooltip content="Success criteria determines the condition that the metric must meet to be considered successful">
          <button type="button" className="inline-flex" aria-label="Info">
            <Info className={INFO_ICON_STYLES} />
          </button>
        </Tooltip>
      </div>
      {existingTargetCondition ? (
        <>
          <div className="flex flex-row items-center justify-between">
            <span className="text-sm text-coval-text-muted">
              <span className="italic">
                {metricRecordData.metric_display_name ||
                  metricRecordData.metric_name ||
                  "New Metric"}
              </span>{" "}
              succeeds when
            </span>
            <Button
              icon
              variant="ghost"
              onClick={removeTargetCondition}
              className="text-coval-text-muted hover:text-coval-danger-text"
              tooltip="Remove success criteria"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-row w-full items-center pl-1">
            <CornerDownRight className="w-4 h-4 text-coval-text-muted mr-1" />
            <div className="flex flex-row gap-2 w-full">
              <Button
                disabled
                variant="secondary"
                className="px-4 rounded-md shrink-0 text-coval-text-muted"
              >
                <span className="font-medium">
                  {metricRecordData.metric_display_name ||
                    metricRecordData.metric_name ||
                    "New Metric"}
                </span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    className="rounded-md shrink-0"
                    disabled={
                      metricType === "binary" || metricType === "boolean"
                    }
                  >
                    <span className="font-medium">
                      {(currentOperators as Record<string, string>)[
                        comparisonOperator
                      ] || ""}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {Object.entries(currentOperators).map(([key, label]) => (
                    <DropdownMenuItem
                      key={key}
                      onSelect={() => {
                        const newOperator = key as ComparisonOperator
                        if (metricType === "numerical") {
                          updateTargetMetadata(newOperator, targetFloat)
                        } else if (metricType === "boolean") {
                          updateTargetMetadata(
                            newOperator,
                            targetBooleanValue ? 1 : 0,
                          )
                        } else if (metricType === "binary") {
                          updateTargetMetadata(
                            newOperator,
                            undefined,
                            new Set([targetBinaryValue]),
                          )
                        } else {
                          updateTargetMetadata(
                            newOperator,
                            undefined,
                            metricType === "string"
                              ? targetStringValues
                              : targetCategoricalValues,
                          )
                        }
                      }}
                    >
                      <span className="font-medium">{label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {(() => {
                switch (metricType) {
                  case "binary":
                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="secondary"
                            className="rounded-md shrink-0"
                          >
                            <span className="font-medium">
                              {targetBinaryValue}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onSelect={() =>
                              updateTargetMetadata(
                                comparisonOperator,
                                undefined,
                                new Set([ANNOTATION_VALUE.YES]),
                              )
                            }
                          >
                            YES
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() =>
                              updateTargetMetadata(
                                comparisonOperator,
                                undefined,
                                new Set([ANNOTATION_VALUE.NO]),
                              )
                            }
                          >
                            NO
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )
                  case "categorical":
                    return (
                      <MultiSelect
                        aria-label="Success categories"
                        options={categoricalOptions}
                        value={selectedCategoricalValues}
                        onValueChange={(newValues) => {
                          updateTargetMetadata(
                            comparisonOperator,
                            undefined,
                            newValues,
                          )
                        }}
                        placeholder="Select categories..."
                        className="flex-1 min-w-0"
                      />
                    )
                  case "string":
                    return (
                      <div className="flex flex-1 flex-col gap-2">
                        {editableStringValues.map((value, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              aria-label={`Success value ${index + 1}`}
                              placeholder="Enter value..."
                              value={value}
                              onChange={(event) => {
                                const values = [...editableStringValues]
                                values[index] = event.target.value
                                updateTargetMetadata(
                                  comparisonOperator,
                                  undefined,
                                  values,
                                )
                              }}
                            />
                            {editableStringValues.length > 1 && (
                              <Button
                                icon
                                variant="ghost"
                                aria-label={`Remove success value ${index + 1}`}
                                tooltip={`Remove success value ${index + 1}`}
                                onClick={() => {
                                  updateTargetMetadata(
                                    comparisonOperator,
                                    undefined,
                                    editableStringValues.filter(
                                      (_, valueIndex) => valueIndex !== index,
                                    ),
                                  )
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          variant="secondary"
                          className="self-start"
                          onClick={() => {
                            updateTargetMetadata(
                              comparisonOperator,
                              undefined,
                              [...editableStringValues, ""],
                            )
                          }}
                        >
                          Add value
                        </Button>
                      </div>
                    )
                  case "boolean":
                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="secondary"
                            className="rounded-md shrink-0"
                          >
                            <span className="font-medium">
                              {targetBooleanValue ? "True" : "False"}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onSelect={() =>
                              updateTargetMetadata(comparisonOperator, 1)
                            }
                          >
                            True
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() =>
                              updateTargetMetadata(comparisonOperator, 0)
                            }
                          >
                            False
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )
                  case "numerical":
                  default:
                    return (
                      <div className="flex flex-1 min-w-0 flex-col gap-1">
                        <NumberInput
                          aria-label="Success threshold"
                          aria-describedby={
                            isThresholdEmpty ? thresholdMessageId : undefined
                          }
                          hideStepper
                          placeholder="Enter value..."
                          value={targetFloat}
                          error={isThresholdEmpty}
                          onValueChange={(value) => {
                            setIsThresholdEmpty(value === undefined)
                            updateTargetMetadata(comparisonOperator, value)
                          }}
                          onBlur={() => setIsThresholdEmpty(false)}
                        />
                        {isThresholdEmpty && (
                          <p
                            id={thresholdMessageId}
                            className="text-xs text-coval-danger-text"
                          >
                            Enter a number. To drop the threshold, remove the
                            success criteria.
                          </p>
                        )}
                      </div>
                    )
                }
              })()}
            </div>
          </div>
        </>
      ) : (
        <Button
          variant="secondary"
          className="w-56"
          onClick={assignDefaultTargetCondition}
        >
          <ListFilter className="w-4 h-4" />
          Add Success Criteria
        </Button>
      )}
    </div>
  )
}
